'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CaloriLogo from '@/components/ui/CaloriLogo/CaloriLogo';

// ---------------------------------------------------------------------------
// Marketing-exhibition kiosk: a prize wheel driven by a single big hardware
// button (a USB button that sends Enter; Space and a click/tap also work).
//
// Standalone page — not part of the localised site, so the copy is English
// only and lives here rather than in dictionaries/{en,fi}.json. Both
// /en/roulette and /fi/roulette render this same English text; the site's
// language switcher does not affect it.
//
// Designed for a laptop mirrored to a big screen in landscape. Press F11 (or
// Cmd+Ctrl+F) for fullscreen before the doors open.
//
// EVERYTHING YOU'D WANT TO CHANGE IS IN THE CONFIG BLOCK BELOW.
// ---------------------------------------------------------------------------

/** Hardcoded on purpose — this only keeps the page from being stumbled upon. */
const ACCESS_CODE = 'calori-messut';

/**
 * The jackpot ("1 free week of meals") is awarded exactly ONCE PER DAY, at a
 * random moment inside this window (laptop local time, 24h clock). On the
 * first load of each day the kiosk rolls a target time and stores it; the
 * first spin at or after that time wins. Set this to your show hours — and
 * end it comfortably before the doors close, or a quiet last hour could mean
 * nobody is left to spin and the jackpot goes unclaimed.
 *
 * Staff reset: open the page with ?reset=1 to re-roll today's jackpot.
 */
const JACKPOT_WINDOW = { startHour: 11, endHour: 13 };

/**
 * How long a result stays on screen before the kiosk returns to idle. The
 * reveal always runs its full time — the big button cannot skip it, so nobody
 * loses their prize to the next person leaning on the button. Staff can cut a
 * reveal short with Esc.
 */
const HOLD_MS = { miss: 4000, snack: 5000, win: 6000, jackpot: 10000 };

const SPIN_MS = 5200;
const MIN_TURNS = 6;

/**
 * The new Calori brand palette. Kept local to the kiosk deliberately — the
 * rest of this app still runs on the old dark-green tokens, and a legacied
 * codebase is the wrong place to half-swap a theme. To take these app-wide,
 * lift them into styles/main.css as the `--color-*` custom properties named
 * below and map them in tailwind.config.js.
 */
const BRAND = {
  offwhite: '#F7F6F3', // --color-offwhite   page background, light text on dark
  cream: '#F0EDE7', // --color-cream         header bar, section bg, card fills
  sage: '#DCE0D6', // --color-sage           waitlist shell background
  darkgrey: '#454545', // --color-darkgrey
  olive: '#848009', // --color-olive         why-calori accents, hero panel
  oliveLight: '#C9BF55', // --color-olive-light  why-calori help banner
  red: '#D96338', // --color-brand-red      the logo mark / primary accent
  orange: '#F29900', // --color-brand-orange   FAQ help banner
  yellow: '#FFD900', // --color-brand-yellow   menu help banner
  yellowBright: '#FFFC65', // --color-yellow-bright  "Recommended" chip
  black: '#000000',
  white: '#FFFFFF'
} as const;

// --- Prize icons -----------------------------------------------------------
// Simple, bold line marks — they need to read from across a hall.

type IconProps = { className?: string };

function SnackIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect
        x="6"
        y="16"
        width="36"
        height="16"
        rx="6"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        d="M14 22.5h2M21 22.5h2M28 22.5h2M35 22.5h2"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M14 27.5h2M21 27.5h2M28 27.5h2M35 27.5h2"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** A meal bowl with the count stamped in it. */
function MealIcon({ className, count }: IconProps & { count: number }) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M6 22h36c0 9.94-8.06 18-18 18S6 31.94 6 22Z"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M3 22h42"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <text
        x="24"
        y="36"
        textAnchor="middle"
        fill="currentColor"
        fontSize="14"
        fontWeight="700"
      >
        {count}×
      </text>
      <path
        d="M18 13c0-3 3-3 3-6M27 13c0-3 3-3 3-6"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** A week of meals — a calendar with seven filled days. */
function WeekIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect
        x="6"
        y="10"
        width="36"
        height="32"
        rx="5"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path d="M6 20h36" stroke="currentColor" strokeWidth="3" />
      <path
        d="M16 6v8M32 6v8"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="14" cy="28" r="2.5" fill="currentColor" />
      <circle cx="24" cy="28" r="2.5" fill="currentColor" />
      <circle cx="34" cy="28" r="2.5" fill="currentColor" />
      <circle cx="14" cy="36" r="2.5" fill="currentColor" />
      <circle cx="24" cy="36" r="2.5" fill="currentColor" />
      <circle cx="34" cy="36" r="2.5" fill="currentColor" />
      <circle cx="24" cy="15" r="2.5" fill="currentColor" />
    </svg>
  );
}

function TryAgainIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M40 24a16 16 0 1 1-4.7-11.3"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M40 6v10H30"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// --- Prizes ----------------------------------------------------------------

type Celebration = 'miss' | 'snack' | 'win' | 'jackpot';

type Prize = {
  /** Short label drawn on the wheel — keep it to ~2 short words per line. */
  short: string[];
  /** Headline on the result card. */
  title: string;
  /** Sub-line on the result card. */
  desc: string;
  /**
   * Relative likelihood. Higher = more common. Weight 0 means the segment is
   * never drawn at random — the jackpot is time-gated, not luck-gated.
   */
  weight: number;
  /** Which reveal treatment the result gets. */
  celebration: Celebration;
  icon: (props: IconProps) => JSX.Element;
  /** Wheel segment colour. */
  fill: string;
  /** Label colour on that segment. */
  ink: string;
};

// Wedge colours alternate light/saturated so no two neighbours read as one
// block, and every `ink` is the high-contrast choice for its `fill`.
const PRIZES: Prize[] = [
  {
    short: ['Free', 'snack'],
    title: 'A free snack',
    desc: 'On us, just for playing.',
    weight: 22,
    celebration: 'snack',
    icon: SnackIcon,
    fill: BRAND.yellow,
    ink: BRAND.black
  },
  {
    short: ['Try', 'again'],
    title: 'No win this time',
    desc: 'Have a word with us at the desk — you can have another go.',
    weight: 15,
    celebration: 'miss',
    icon: TryAgainIcon,
    fill: BRAND.sage,
    ink: BRAND.black
  },
  {
    short: ['2 free', 'meals'],
    title: '2 free meals',
    desc: 'A card for 2 free meals in your order.',
    weight: 12,
    celebration: 'win',
    icon: (props) => <MealIcon {...props} count={2} />,
    fill: BRAND.olive,
    ink: BRAND.offwhite
  },
  {
    short: ['Free', 'snack'],
    title: 'A free snack',
    desc: 'On us, just for playing.',
    weight: 22,
    celebration: 'snack',
    icon: SnackIcon,
    fill: BRAND.cream,
    ink: BRAND.black
  },
  {
    short: ['3 free', 'meals'],
    title: '3 free meals',
    desc: 'A card for 3 free meals in your order.',
    weight: 7,
    celebration: 'win',
    icon: (props) => <MealIcon {...props} count={3} />,
    fill: BRAND.orange,
    ink: BRAND.black
  },
  {
    // The jackpot wears the primary brand accent. Weight 0 — awarded only at
    // the once-a-day moment picked in JACKPOT_WINDOW.
    short: ['1 FREE', 'WEEK'],
    title: '1 free week of meals',
    desc: 'A whole week of Calori, on the house.',
    weight: 0,
    celebration: 'jackpot',
    icon: WeekIcon,
    fill: BRAND.red,
    ink: BRAND.offwhite
  },
  {
    short: ['Try', 'again'],
    title: 'No win this time',
    desc: 'Have a word with us at the desk — you can have another go.',
    weight: 15,
    celebration: 'miss',
    icon: TryAgainIcon,
    fill: BRAND.darkgrey,
    ink: BRAND.offwhite
  },
  {
    short: ['4 free', 'meals'],
    title: '4 free meals',
    desc: 'A card for 4 free meals in your order.',
    weight: 4,
    celebration: 'win',
    icon: (props) => <MealIcon {...props} count={4} />,
    fill: BRAND.oliveLight,
    ink: BRAND.black
  }
];

/** What the banner under the wheel advertises. */
const BANNER = [
  { icon: SnackIcon, label: 'Free snack' },
  {
    icon: (p: IconProps) => <MealIcon {...p} count={2} />,
    label: '2 free meals'
  },
  {
    icon: (p: IconProps) => <MealIcon {...p} count={3} />,
    label: '3 free meals'
  },
  {
    icon: (p: IconProps) => <MealIcon {...p} count={4} />,
    label: '4 free meals'
  },
  { icon: WeekIcon, label: '1 free week' }
];

const JACKPOT_INDEX = PRIZES.findIndex((p) => p.celebration === 'jackpot');

// ---------------------------------------------------------------------------

const SEGMENTS = PRIZES.length;
const SEG_ANGLE = 360 / SEGMENTS;
const UNLOCK_KEY = 'calori-roulette-unlocked';
const JACKPOT_KEY = 'calori-roulette-jackpot';

/**
 * Weighted pick over PRIZES, returning the winning index. Weight-0 segments
 * (the jackpot) are never returned.
 */
function pickPrizeIndex(): number {
  const total = PRIZES.reduce((sum, prize) => sum + prize.weight, 0);
  let roll = Math.random() * total;
  for (let i = 0; i < PRIZES.length; i++) {
    if (PRIZES[i].weight <= 0) continue;
    roll -= PRIZES[i].weight;
    if (roll <= 0) return i;
  }
  return PRIZES.findIndex((p) => p.weight > 0);
}

// --- Once-a-day jackpot scheduling -----------------------------------------

type JackpotState = { day: string; at: number; awarded: boolean };

function todayKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/** Roll a fresh target moment inside today's window and persist it. */
function rollJackpot(): JackpotState {
  const now = Date.now();
  const start = new Date();
  start.setHours(JACKPOT_WINDOW.startHour, 0, 0, 0);
  const end = new Date();
  end.setHours(JACKPOT_WINDOW.endHour, 0, 0, 0);

  // Never schedule in the past: if the kiosk boots mid-window, draw from the
  // remaining window. If it boots after the window has closed entirely, fall
  // back to the next hour so the jackpot still lands rather than firing on
  // the very first spin.
  const from = Math.max(now, start.getTime());
  const to = end.getTime();
  const at =
    to > from
      ? from + Math.random() * (to - from)
      : now + Math.random() * 3600_000;

  const state: JackpotState = { day: todayKey(), at, awarded: false };
  try {
    localStorage.setItem(JACKPOT_KEY, JSON.stringify(state));
  } catch {
    // Private browsing / storage disabled — the jackpot just won't persist
    // across reloads. Not worth breaking the kiosk over.
  }
  return state;
}

function loadJackpot(): JackpotState {
  try {
    const raw = localStorage.getItem(JACKPOT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as JackpotState;
      if (parsed && parsed.day === todayKey()) return parsed;
    }
  } catch {
    // fall through to a fresh roll
  }
  return rollJackpot();
}

function markJackpotAwarded(state: JackpotState) {
  const next = { ...state, awarded: true };
  try {
    localStorage.setItem(JACKPOT_KEY, JSON.stringify(next));
  } catch {
    // see rollJackpot
  }
  return next;
}

// --- Wheel geometry --------------------------------------------------------

/**
 * Absolute rotation (degrees, always increasing) that parks segment `index`
 * under the pointer at 12 o'clock. Segment i occupies [i·SEG, (i+1)·SEG)
 * measured clockwise from the top, so it is centred under the pointer when
 * the wheel has turned 360 − (i·SEG + SEG/2).
 */
function rotationFor(index: number, current: number): number {
  const jitter = (Math.random() - 0.5) * SEG_ANGLE * 0.7;
  const target = 360 - (index * SEG_ANGLE + SEG_ANGLE / 2) + jitter;
  const delta = (((target - current) % 360) + 360) % 360;
  return current + MIN_TURNS * 360 + delta;
}

/** SVG wedge path for segment `index` on a circle of radius r centred at c. */
function wedgePath(index: number, c: number, r: number): string {
  const toRad = (deg: number) => ((deg - 90) * Math.PI) / 180;
  const a0 = toRad(index * SEG_ANGLE);
  const a1 = toRad((index + 1) * SEG_ANGLE);
  const x0 = c + r * Math.cos(a0);
  const y0 = c + r * Math.sin(a0);
  const x1 = c + r * Math.cos(a1);
  const y1 = c + r * Math.sin(a1);
  const largeArc = SEG_ANGLE > 180 ? 1 : 0;
  return `M ${c} ${c} L ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1} Z`;
}

function Wheel({ rotation }: { rotation: number }) {
  const C = 200;
  const R = 194;

  return (
    <svg
      viewBox="0 0 400 400"
      className="w-full h-full drop-shadow-2xl"
      aria-hidden="true"
    >
      <g
        style={{
          transform: `rotate(${rotation}deg)`,
          transformOrigin: '200px 200px',
          transition: `transform ${SPIN_MS}ms cubic-bezier(0.16, 0.84, 0.16, 1)`
        }}
      >
        {PRIZES.map((prize, i) => (
          <path
            key={`wedge-${i}`}
            d={wedgePath(i, C, R)}
            fill={prize.fill}
            stroke={BRAND.white}
            strokeWidth={2.5}
          />
        ))}
        {PRIZES.map((prize, i) => {
          const mid = i * SEG_ANGLE + SEG_ANGLE / 2;
          // Labels sit upright within their own wedge. No fixed-in-wheel
          // layout can keep every label upright at an arbitrary stop angle
          // (radial text inverts on the left half instead) — but this one
          // puts the *winning* label, the only one anyone reads, perfectly
          // horizontal under the pointer every time.
          const lines = prize.short;
          return (
            <g key={`label-${i}`} transform={`rotate(${mid} ${C} ${C})`}>
              {lines.map((line, j) => (
                <text
                  key={j}
                  x={C}
                  y={C - R * 0.66 + j * 19}
                  textAnchor="middle"
                  fill={prize.ink}
                  fontSize={16}
                  fontWeight={500}
                  letterSpacing="0.01em"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </g>

      {/* Rim — the light wedges need it to read as a wheel on an off-white page */}
      <circle
        cx={C}
        cy={C}
        r={R}
        fill="none"
        stroke={BRAND.black}
        strokeWidth={3}
      />

      {/* Hub — outside the rotating group, so the wordmark stays upright */}
      <circle cx={C} cy={C} r={40} fill={BRAND.offwhite} />
      <circle
        cx={C}
        cy={C}
        r={40}
        fill="none"
        stroke={BRAND.black}
        strokeWidth={3}
      />
      <CaloriLogo
        x={C - 27}
        y={C - 8.6}
        width={54}
        height={17.2}
        fill={BRAND.black}
      />
    </svg>
  );
}

// --- Celebration -----------------------------------------------------------

function Confetti({ count, colors }: { count: number; colors: string[] }) {
  // Randomised once per mount — the component only ever mounts client-side,
  // after a spin resolves, so there is no hydration mismatch to worry about.
  const pieces = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.7,
        duration: 2.4 + Math.random() * 1.8,
        drift: (Math.random() - 0.5) * 240,
        spin: 360 + Math.random() * 720,
        size: 8 + Math.random() * 12,
        color: colors[i % colors.length],
        round: Math.random() > 0.6
      })),
    [count, colors]
  );

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((piece, i) => (
        <span
          key={i}
          className="absolute block"
          style={{
            left: `${piece.left}%`,
            top: '-8%',
            width: piece.size,
            height: piece.round ? piece.size : piece.size * 0.5,
            background: piece.color,
            borderRadius: piece.round ? '9999px' : '2px',
            animation: `calori-confetti ${piece.duration}s linear ${piece.delay}s forwards`,
            ['--drift' as string]: `${piece.drift}px`,
            ['--spin' as string]: `${piece.spin}deg`
          }}
        />
      ))}
    </div>
  );
}

/** Keyframes live with the component so the kiosk stays self-contained. */
function KioskStyles() {
  return (
    <style>{`
      @keyframes calori-confetti {
        0%   { transform: translate3d(0,0,0) rotate(0deg); opacity: 1; }
        100% { transform: translate3d(var(--drift), 115vh, 0) rotate(var(--spin)); opacity: 1; }
      }
      @keyframes calori-pop {
        0%   { transform: scale(0.82); opacity: 0; }
        60%  { transform: scale(1.04); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes calori-jackpot-pop {
        0%   { transform: scale(0.6) rotate(-4deg); opacity: 0; }
        50%  { transform: scale(1.12) rotate(2deg); opacity: 1; }
        70%  { transform: scale(0.98) rotate(-1deg); }
        100% { transform: scale(1) rotate(0deg); opacity: 1; }
      }
      @keyframes calori-jackpot-glow {
        0%, 100% { opacity: 0.25; }
        50%      { opacity: 0.6; }
      }
      @keyframes calori-miss {
        0%, 100% { transform: translateX(0); }
        20%      { transform: translateX(-12px); }
        40%      { transform: translateX(10px); }
        60%      { transform: translateX(-6px); }
        80%      { transform: translateX(4px); }
      }
      @keyframes calori-idle-bob {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-6px); }
      }
    `}</style>
  );
}

// --- Page ------------------------------------------------------------------

export default function RouletteGame() {
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);

  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<Prize | null>(null);

  const jackpot = useRef<JackpotState | null>(null);
  const spinTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem(UNLOCK_KEY) === '1') setUnlocked(true);

    // Staff escape hatch: ?reset=1 re-rolls today's jackpot.
    if (new URLSearchParams(window.location.search).get('reset') === '1') {
      jackpot.current = rollJackpot();
    } else {
      jackpot.current = loadJackpot();
    }
  }, []);

  useEffect(
    () => () => {
      if (spinTimer.current) clearTimeout(spinTimer.current);
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    []
  );

  // The kiosk is a fixed overlay, but the site's navbar and footer still sit
  // behind it and make the document taller than the screen. Lock the body so
  // a stray scroll can never shift what's on the big screen.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const dismiss = useCallback(() => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
    setResult(null);
  }, []);

  const spin = useCallback(() => {
    if (spinning) return;
    // A reveal is never dismissed by the big button. Someone reading their
    // prize should not lose it because the next person leans on the button,
    // so the reveal runs its full HOLD_MS. Staff can cut it short with Esc.
    if (result) return;

    // Is this the once-a-day jackpot moment?
    const state = jackpot.current;
    const isJackpot =
      !!state && !state.awarded && Date.now() >= state.at && JACKPOT_INDEX >= 0;
    if (isJackpot && state) jackpot.current = markJackpotAwarded(state);

    const index = isJackpot ? JACKPOT_INDEX : pickPrizeIndex();
    const prize = PRIZES[index];

    setSpinning(true);
    setRotation((current) => rotationFor(index, current));

    spinTimer.current = setTimeout(() => {
      setSpinning(false);
      setResult(prize);
      const hold = HOLD_MS[prize.celebration];
      // Auto-return to idle so the next person can just walk up and play.
      holdTimer.current = setTimeout(() => setResult(null), hold);
    }, SPIN_MS);
  }, [spinning, result]);

  useEffect(() => {
    if (!unlocked) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // Staff key: Esc cuts a reveal short. Deliberately NOT a key the big
      // button can send, and deliberately not hinted on screen — the public
      // should never be able to clear someone else's prize.
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
        return;
      }

      // The big hardware button sends Enter. Space and NumpadEnter are
      // accepted too so a plain keyboard works during setup.
      const isTrigger =
        event.code === 'Enter' ||
        event.code === 'NumpadEnter' ||
        event.key === 'Enter' ||
        event.code === 'Space' ||
        event.key === ' ';
      if (!isTrigger) return;
      event.preventDefault(); // stop the page from scrolling
      spin();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [unlocked, spin, dismiss]);

  const submitCode = (event: React.FormEvent) => {
    event.preventDefault();
    if (codeInput.trim().toLowerCase() === ACCESS_CODE) {
      sessionStorage.setItem(UNLOCK_KEY, '1');
      setUnlocked(true);
      return;
    }
    setCodeError(true);
    setCodeInput('');
  };

  if (!unlocked) {
    return (
      <div className="fixed inset-0 z-[60] bg-[#F7F6F3] text-black flex items-center justify-center p-6">
        <form
          onSubmit={submitCode}
          className="w-full max-w-md bg-[#F0EDE7] rounded-3xl p-6 flex flex-col gap-6"
        >
          <div>
            <CaloriLogo className="h-7 w-auto text-black" />
            <h1 className="text-3xl md:text-4xl mt-4">Prize wheel</h1>
            <p className="text-[#454545] mt-2">Enter the access code.</p>
          </div>
          <input
            autoFocus
            type="password"
            value={codeInput}
            onChange={(event) => {
              setCodeInput(event.target.value);
              setCodeError(false);
            }}
            placeholder="Access code"
            className="w-full rounded-3xl bg-white border border-black/15 px-6 py-4 text-black placeholder:text-black/30 outline-none focus:border-[#D96338]"
          />
          {codeError && (
            <p className="text-[#D96338] -mt-3">Wrong code. Try again.</p>
          )}
          <button
            type="submit"
            className="w-full rounded-3xl bg-black text-[#F7F6F3] py-4 font-medium hover:bg-[#454545] transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  const ResultIcon = result?.icon;
  const isJackpotResult = result?.celebration === 'jackpot';

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#F7F6F3] text-black flex flex-col overflow-hidden select-none cursor-pointer"
      onClick={spin}
      role="button"
      tabIndex={0}
      aria-label="Spin the prize wheel"
    >
      <KioskStyles />

      {/* Header bar */}
      <header className="shrink-0 bg-[#F0EDE7] border-b-2 border-black">
        <div className="flex items-center justify-between px-8 py-4">
          <CaloriLogo className="h-8 xl:h-10 w-auto text-black" />
          <div className="flex items-center gap-2">
            {[
              BRAND.red,
              BRAND.orange,
              BRAND.yellow,
              BRAND.olive,
              BRAND.sage
            ].map((color) => (
              <span
                key={color}
                className="block w-3 h-3 xl:w-4 xl:h-4 rounded-full"
                style={{ background: color }}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Stage */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-[2vh] px-6 py-[2vh]">
        <div className="text-center">
          <h1 className="text-[6vh] xl:text-[7vh] leading-none font-medium tracking-tight">
            SPIN TO WIN
          </h1>
          <span className="inline-block mt-[1.4vh] rounded-full bg-[#D96338] text-[#F7F6F3] px-5 py-1.5 text-[2vh] font-medium tracking-[0.18em]">
            FREE ENTRY
          </span>
        </div>

        <div className="relative h-[46vh] aspect-square">
          {/* Pointer */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
            <svg width="38" height="34" viewBox="0 0 38 34" aria-hidden="true">
              <path d="M19 34 L1 0 L37 0 Z" fill="#000000" />
            </svg>
          </div>
          <Wheel rotation={rotation} />
        </div>

        <p
          className={`text-center text-[2.2vh] text-[#454545] transition-opacity ${
            spinning ? 'opacity-0' : 'opacity-100'
          }`}
          style={
            spinning
              ? undefined
              : { animation: 'calori-idle-bob 2.4s ease-in-out infinite' }
          }
        >
          Press the{' '}
          <span className="text-[#D96338] font-medium">big button</span> to spin
        </p>
      </div>

      {/* Prize banner */}
      <footer className="shrink-0 bg-[#F0EDE7] border-t-2 border-black">
        <div className="flex items-stretch justify-center gap-3 xl:gap-4 px-6 py-[1.6vh]">
          {BANNER.map((entry, i) => {
            const Icon = entry.icon;
            const isWeek = i === BANNER.length - 1;
            return (
              <div
                key={entry.label}
                className={`flex items-center gap-3 rounded-2xl border-2 border-black px-4 py-[1.2vh] ${
                  isWeek
                    ? 'bg-[#D96338] text-[#F7F6F3]'
                    : 'bg-[#F7F6F3] text-black'
                }`}
              >
                <Icon className="h-[3.4vh] w-[3.4vh]" />
                <span className="text-[1.9vh] font-medium whitespace-nowrap">
                  {entry.label}
                </span>
              </div>
            );
          })}
        </div>
      </footer>

      {/* Reveal */}
      {result && (
        <div
          data-reveal={result.celebration}
          // z-20 keeps the reveal above the wheel's pointer, which is z-10 and
          // would otherwise paint through the overlay.
          className="absolute inset-0 z-20 flex items-center justify-center p-6"
          style={{
            background: isJackpotResult
              ? 'rgba(217,99,56,0.94)'
              : 'rgba(247,246,243,0.9)',
            backdropFilter: 'blur(6px)'
          }}
        >
          {isJackpotResult && (
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(circle at 50% 45%, #FFD900 0%, transparent 62%)',
                animation: 'calori-jackpot-glow 1.1s ease-in-out infinite'
              }}
            />
          )}

          {result.celebration === 'jackpot' && (
            <Confetti
              count={140}
              colors={[
                BRAND.yellow,
                BRAND.yellowBright,
                BRAND.offwhite,
                BRAND.orange,
                BRAND.white
              ]}
            />
          )}
          {result.celebration === 'win' && (
            <Confetti
              count={70}
              colors={[BRAND.red, BRAND.orange, BRAND.yellow, BRAND.olive]}
            />
          )}

          <div
            className={`relative text-center rounded-3xl border-2 px-[5vh] py-[4vh] max-w-[80vw] ${
              isJackpotResult
                ? 'border-[#F7F6F3] bg-[#D96338] text-[#F7F6F3]'
                : 'border-black bg-[#F0EDE7] text-black'
            }`}
            style={{
              animation: isJackpotResult
                ? 'calori-jackpot-pop 0.7s cubic-bezier(0.2,1.1,0.3,1) both'
                : result.celebration === 'miss'
                  ? 'calori-miss 0.5s ease-in-out both'
                  : 'calori-pop 0.4s cubic-bezier(0.2,1.1,0.3,1) both'
            }}
          >
            {ResultIcon && (
              <ResultIcon
                className={`mx-auto h-[9vh] w-[9vh] ${
                  isJackpotResult ? 'text-[#F7F6F3]' : 'text-black'
                }`}
              />
            )}

            <p
              className={`mt-[2vh] uppercase tracking-[0.22em] text-[1.9vh] font-medium ${
                isJackpotResult
                  ? 'text-[#FFD900]'
                  : result.celebration === 'miss'
                    ? 'text-[#454545]'
                    : 'text-[#D96338]'
              }`}
            >
              {isJackpotResult
                ? 'JACKPOT'
                : result.celebration === 'miss'
                  ? 'So close'
                  : 'You won'}
            </p>

            <h2 className="text-[7vh] leading-[1.05] mt-[1.5vh]">
              {result.title}
            </h2>

            <p
              className={`mt-[2vh] text-[2.4vh] ${
                isJackpotResult ? 'text-[#F7F6F3]/90' : 'text-[#454545]'
              }`}
            >
              {result.desc}
            </p>

            {result.celebration !== 'miss' && (
              <p
                className={`mt-[2.4vh] text-[2.6vh] font-medium ${
                  isJackpotResult ? 'text-[#FFD900]' : 'text-black'
                }`}
              >
                Claim your prize from the desk.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
